/**
 * Тип записи по первым байтам (13.09.2026).
 *
 * 🔴 ЗАЧЕМ. Вечером 12.09 на проде 18 из 19 отказанных записей начинались не с
 * заголовка: у iPhone сразу `moof`, у Chrome — середина кластера. Их подмешивал
 * второй рекордер, оставшийся жить после двойного запуска
 * (`docs/CHECK-2026-09-13-RECORD-STATS.md`). Такой файл не играет, не
 * декодируется и не проходит сервер — значит, и показывать его ученику с кнопкой
 * «Отправить» нельзя.
 *
 * ⚠️ ПОДПИСИ НЕ ВЕРИМ. Черновик подписывал mp4 с iPhone как `audio/webm`: тип
 * брался у рекордера до старта, а Safari его в этот момент не называет.
 *
 * ⚠️ НАБОР — КАК У СЕРВЕРА (files-service, договор `record-integrity.md` §1): всё,
 * чего он не опознаёт, он отвергает как «неизвестный контейнер». Опознанное
 * начало — условие необходимое, но не достаточное: играет ли файл, решают декод и
 * `lib/audioPlayable.ts`.
 */

export type SniffedAudioType =
  | 'audio/mp4'
  | 'audio/webm'
  | 'audio/ogg'
  | 'audio/wav'
  | 'audio/flac'
  | 'audio/aac'
  | 'audio/mpeg';

/** Сколько байт читать: самой длинной сигнатуре (wav) нужно двенадцать. */
export const SNIFF_BYTES = 16;

const at = (head: Uint8Array, offset: number, text: string) =>
  head.length >= offset + text.length && Array.from(text).every((ch, i) => head[offset + i] === ch.charCodeAt(0));

export function sniffAudioType(head: Uint8Array): SniffedAudioType | null {
  if (!head || head.length < 4) return null;
  if (at(head, 4, 'ftyp')) return 'audio/mp4';
  if (head[0] === 0x1a && head[1] === 0x45 && head[2] === 0xdf && head[3] === 0xa3) return 'audio/webm';
  if (at(head, 0, 'OggS')) return 'audio/ogg';
  if (at(head, 0, 'RIFF') && at(head, 8, 'WAVE')) return 'audio/wav';
  if (at(head, 0, 'fLaC')) return 'audio/flac';
  if (at(head, 0, 'ID3')) return 'audio/mpeg';
  if (head[0] === 0xff && (head[1] & 0xf6) === 0xf0) return 'audio/aac';
  if (head[0] === 0xff && (head[1] & 0xe0) === 0xe0) return 'audio/mpeg';
  return null;
}

/** То же для `Blob`. Никогда не бросает: не прочиталось — «не опознан». */
export async function sniffBlob(blob: Blob): Promise<SniffedAudioType | null> {
  try {
    if (!blob || !blob.size) return null;
    const buf = await blob.slice(0, SNIFF_BYTES).arrayBuffer();
    return sniffAudioType(new Uint8Array(buf));
  } catch {
    return null;
  }
}
